import { useState, useEffect, useCallback, useRef } from 'react'
import {
  AlertTriangle,
  BusFront,
  Info,
  Search,
  X,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { getHeroPreviewHeroMode, useHeroPreview } from '../context/HeroPreviewContext'
import { isSemesterOver } from '../utils/semesterUtils'
import { shuttleData }        from '../data/shuttleData'
import { PERIOD_TIMES } from '../data/periodTimes'
import {
  getFavoriteShuttleRoutes,
  getDefaultCommuteRoute,
  toggleFavoriteRoute,
} from '../utils/shuttleFavoriteUtils'
import { loadEffectiveTimetable } from '../lib/userTimetable'
import { COURSE_REGISTRATION_SCHEDULE } from '../data/courseRegistrationSchedule'
import {
  getCurrentMinutes,
  getNextShuttleTime,
} from '../utils/shuttle/shuttleTimeUtils'
import { getUnifiedShuttleRoutes } from '../utils/shuttle'
import { buildStationGroups, filterStationGroups } from '../utils/shuttle/shuttleStationGroups'
import {
  getInitialExpandedStationKey,
  getInitialExpandedStationKeys,
  getVisibleExpandedStationKeys,
  toggleExpandedStationKeys,
} from '../utils/shuttle/initialExpandedStation'
import {
  getShuttleRouteMotionLabel,
  getShuttleRouteMotionPhase,
  getShuttleRouteMotionMode,
} from '../utils/shuttleRouteMotion'
import ShuttleStationCard from './ShuttleStationCard'
import ShuttleRouteMotion from './home/ShuttleRouteMotion'

// ── 셔틀 노선 정렬 ────────────────────────────────────────────────────────────

const SHUTTLE_ROUTE_ORDER = [
  '병점역 → 학교',
  '학교 → 병점역',
  '고색역 → 학교',
  '송내역 → 학교',
  '학교 → 송내역',
  '사당역 → 학교',
  '학교 → 사당역',
  '강남역 → 학교',
  '교내 셔틀버스',
]

const SHUTTLE_LABEL_ALIASES = {
  '학교 → 병점역': '학교 → 병점역',
  '정문 → 병점역':        '학교 → 병점역',
  '수원대 → 병점역':      '학교 → 병점역',
  '수원대 → 사당역':      '학교 → 사당역',
  '수원대 → 송내역':      '학교 → 송내역',
  '교내 순환':            '교내 셔틀버스',
  '교내 셔틀':            '교내 셔틀버스',
  '교내 순환버스':        '교내 셔틀버스',
}

function getShuttleRouteLabel(route) {
  const raw = route?.routeName || route?.title || route?.name ||
    `${route?.from || ''} → ${route?.to || ''}`
  return SHUTTLE_LABEL_ALIASES[raw] ?? raw
}

export function sortShuttleRoutes(routes = []) {
  return [...routes]
    .map((route, index) => ({ route, index }))
    .sort((a, b) => {
      const aOrder = SHUTTLE_ROUTE_ORDER.indexOf(getShuttleRouteLabel(a.route))
      const bOrder = SHUTTLE_ROUTE_ORDER.indexOf(getShuttleRouteLabel(b.route))
      const safeA  = aOrder === -1 ? 999 : aOrder
      const safeB  = bOrder === -1 ? 999 : bOrder
      if (safeA !== safeB) return safeA - safeB
      return a.index - b.index
    })
    .map(item => item.route)
}

function getRouteSearchText(route) {
  return [
    route.name,
    route.from,
    route.to,
    route.boardingPoint,
    route.boardingPointDetail,
    route.routeSummary,
    route.destinationLabel,
  ].join(' ').toLowerCase()
}

function getRouteNextSchedule(schedules, now = getCurrentMinutes()) {
  let best = null
  for (const item of schedules ?? []) {
    const next = getNextShuttleTime([item.time], now)
    if (!next) continue
    if (!best || next.depMin < best.depMin) {
      best = { item, depMin: next.depMin }
    }
  }
  return best?.item ?? null
}

function getPrimaryShuttleHeroRoute(routes, now = getCurrentMinutes()) {
  let best = null
  for (const route of routes ?? []) {
    const nextSchedule = getRouteNextSchedule(route.schedules, now)
    if (!nextSchedule) continue
    const next = getNextShuttleTime([nextSchedule.time], now)
    if (!next) continue
    if (!best || next.depMin < best.next.depMin) {
      best = { route, nextSchedule, next }
    }
  }

  if (best) return best

  const fallbackRoute = routes.find(route => route.id === 'byeongjeom_to_school') ?? routes[0]
  return fallbackRoute
    ? { route: fallbackRoute, nextSchedule: null, next: null }
    : null
}

export function getRouteHeroState(route, now = getCurrentMinutes(), context = getShuttleContext()) {
  if (!route) return null
  if (context?.isSemesterOver) return { route, nextSchedule: null, next: null, context }
  const nextSchedule = getRouteNextSchedule(route.schedules, now)
  const next = nextSchedule ? getNextShuttleTime([nextSchedule.time], now) : null
  return { route, nextSchedule, next, context }
}

function getRouteHeroCandidate(route, now, context) {
  if (!route) return null
  const state = getRouteHeroState(route, now, context)
  if (!state?.nextSchedule || !state?.next) return null
  return state
}

const DAY_KR = ['일', '월', '화', '수', '목', '금', '토']

function getPeriodMin(period, edge = 'start') {
  const slot = PERIOD_TIMES?.[period]
  const raw = edge === 'end' ? slot?.end : slot?.start
  return timeToMinutes(raw)
}

function getCourseStartMin(course) {
  return timeToMinutes(course?.startTime) ?? getPeriodMin(course?.startPeriod, 'start')
}

function getCourseEndMin(course) {
  return timeToMinutes(course?.endTime) ??
    getPeriodMin(course?.endPeriod ?? course?.startPeriod, 'end') ??
    getCourseStartMin(course)
}

function getTodayClassContext(date = new Date(), uid = null) {
  try {
    const courses = loadEffectiveTimetable(uid, COURSE_REGISTRATION_SCHEDULE.semesterKey)
    if (!Array.isArray(courses) || courses.length === 0) return null

    const today = DAY_KR[date.getDay()]
    const now = date.getHours() * 60 + date.getMinutes()
    const todayCourses = courses
      .filter(course => {
        if (course?.isOnline) return false
        const days = Array.isArray(course?.days) ? course.days : (course?.day ? [course.day] : [])
        return days.includes(today)
      })
      .map(course => ({
        course,
        startMin: getCourseStartMin(course),
        endMin: getCourseEndMin(course),
      }))
      .filter(item => Number.isFinite(item.startMin) && Number.isFinite(item.endMin))
      .sort((a, b) => a.startMin - b.startMin)

    if (todayCourses.length === 0) return null

    const first = todayCourses[0]
    const last = todayCourses[todayCourses.length - 1]
    const current = todayCourses.find(item => item.startMin <= now && now < item.endMin)
    const nextClass = todayCourses.find(item => item.startMin > now)

    if (now < first.startMin) return { direction: 'toSchool', reason: 'beforeFirstClass', label: '오늘 학교 가는 셔틀' }
    if (current) return { direction: 'fromSchool', reason: 'inClass', label: '수업 중이에요' }
    if (now >= last.endMin) return { direction: 'fromSchool', reason: 'afterLastClass', label: '오늘 집 가는 셔틀' }
    if (nextClass) return { direction: 'toSchool', reason: 'betweenClasses', label: '다음 수업 전 셔틀' }
    return null
  } catch {
    return null
  }
}

export function getShuttleContext(date = new Date(), uid = null) {
  if (isSemesterOver(date)) {
    return { direction: 'toSchool', reason: 'semesterOver', label: '2학기 운행 예정', isSemesterOver: true }
  }

  const day = date.getDay()
  const now = date.getHours() * 60 + date.getMinutes()

  if (day === 0 || day === 6) {
    return { direction: 'toSchool', reason: 'weekend', label: '다음 운행 안내', isWeekend: true }
  }

  if (now >= 15 * 60 && now <= 22 * 60 + 30) {
    return { direction: 'fromSchool', reason: 'goHomeWindow', label: '오늘 집 가는 셔틀' }
  }

  if (now >= 5 * 60 && now < 15 * 60) {
    const classContext = getTodayClassContext(date, uid)
    if (classContext) return classContext
    return now < 13 * 60 + 30
      ? { direction: 'toSchool', reason: 'goSchoolWindow', label: '오늘 학교 가는 셔틀' }
      : { direction: 'fromSchool', reason: 'goHomeWindow', label: '오늘 집 가는 셔틀' }
  }

  return { direction: 'toSchool', reason: 'offHours', label: '내일 첫 셔틀' }
}

function isSchoolBoundRoute(route) {
  const text = `${route?.id ?? ''} ${route?.name ?? ''} ${route?.from ?? ''} ${route?.to ?? ''}`
  return !text.includes('school_to_') &&
    (text.includes('→ 학교') || text.includes('→ 수원대') || route?.to?.includes('학교') || route?.to?.includes('수원대'))
}

function isHomeBoundRoute(route) {
  const text = `${route?.id ?? ''} ${route?.name ?? ''} ${route?.from ?? ''} ${route?.to ?? ''}`
  return text.includes('school_to_') ||
    text.includes('학교 →') ||
    text.includes('수원대 →') ||
    route?.from?.includes('학교') ||
    route?.from?.includes('수원대')
}

function getRoutesByContext(routes, direction) {
  if (direction === 'fromSchool') return routes.filter(isHomeBoundRoute)
  return routes.filter(isSchoolBoundRoute)
}

// 캠퍼스맵 외부 셔틀 승차장 POI의 relatedStationId → 정류장별 노선 stationKey(한글역명)
const STATION_ID_TO_KOREAN = {
  byeongjeom: '병점역',
  gosaek:     '고색역',
  songnae:    '송내역',
  sadang:     '사당역',
  gangnam:    '강남역',
}

// 정류장 왕복 쌍: 즐겨찾기 정류장 기준 등교↔하교 노선 매핑
const STATION_ROUTE_PAIRS = {
  byeongjeom: { toSchool: 'byeongjeom_to_school', fromSchool: 'school_to_byeongjeom', stationName: '병점역' },
  songnae:    { toSchool: 'songnae_to_school',    fromSchool: 'school_to_songnae',    stationName: '송내역' },
  sadang:     { toSchool: 'sadang_to_school',     fromSchool: null,                   stationName: '사당역' },
  gosaek:     { toSchool: 'gosaek_to_school',     fromSchool: null,                   stationName: '고색역' },
  gangnam:    { toSchool: 'gangnam_to_school',    fromSchool: null,                   stationName: '강남역' },
}

function getStationPairFromRouteId(routeId) {
  for (const pair of Object.values(STATION_ROUTE_PAIRS)) {
    if (pair.toSchool === routeId || pair.fromSchool === routeId) return pair
  }
  return null
}

function getFavoriteRoundTripRoutes(routes, stationPair, favoriteRouteId) {
  if (!stationPair) {
    return routes.find(route => route.id === favoriteRouteId)
      ? [routes.find(route => route.id === favoriteRouteId)]
      : []
  }
  return [stationPair.toSchool, stationPair.fromSchool]
    .filter(Boolean)
    .map(routeId => routes.find(route => route.id === routeId))
    .filter(Boolean)
}

function getFavoriteEndedRoute(roundTripRoutes, stationPair, favoriteRouteId) {
  const toSchoolRoute = stationPair?.toSchool
    ? roundTripRoutes.find(route => route.id === stationPair.toSchool)
    : null
  return toSchoolRoute ?? roundTripRoutes.find(route => route.id === favoriteRouteId) ?? roundTripRoutes[0] ?? null
}

export function getSmartShuttleHeroRoute(routes, favoriteIds = [], date = new Date(), uid = null) {
  const context = getShuttleContext(date, uid)
  const now = date.getHours() * 60 + date.getMinutes()
  // 노선 우선순위: 1) 기본 통학 노선 2) 즐겨찾기 노선 (홈 히어로와 동일 기준)
  const defaultRouteId = getDefaultCommuteRoute(uid)?.routeId ?? null
  const favoriteRouteId = (defaultRouteId && routes.some(r => r.id === defaultRouteId))
    ? defaultRouteId
    : (favoriteIds[0] ?? null)

  if (favoriteRouteId) {
    const stationPair = getStationPairFromRouteId(favoriteRouteId)
    const favoriteContext = {
      ...context,
      label: '즐겨찾기 정류장',
      isFavoriteHero: true,
      favoriteStationName: stationPair?.stationName,
    }
    const roundTripRoutes = getFavoriteRoundTripRoutes(routes, stationPair, favoriteRouteId)

    if (roundTripRoutes.length > 0) {
      const futureCandidates = context.isWeekend
        ? []
        : roundTripRoutes
            .map(route => getRouteHeroCandidate(route, now, favoriteContext))
            .filter(Boolean)
            .sort((a, b) => a.next.depMin - b.next.depMin)

      if (futureCandidates.length > 0) return futureCandidates[0]

      const endedRoute = getFavoriteEndedRoute(roundTripRoutes, stationPair, favoriteRouteId)
      return getRouteHeroState(endedRoute, now, favoriteContext)
    }
  }

  // 즐겨찾기 없음 → 전체 노선 중 현재 이후 가장 가까운 셔틀만 fallback으로 사용
  const primary = getPrimaryShuttleHeroRoute(routes, now)
  const activeRoute = primary?.route ?? routes[0]
  const fallbackDirection = isHomeBoundRoute(activeRoute) ? 'fromSchool' : 'toSchool'
  return getRouteHeroState(activeRoute, now, {
    ...context,
    direction: fallbackDirection,
    label: '다음 운행 안내',
  }) ?? getPrimaryShuttleHeroRoute(routes, now)
}

export function getPreviewShuttleHero(routes, previewMode) {
  if (!routes?.length) return null
  const schoolRoutes = routes.filter(isSchoolBoundRoute)
  const homeRoutes = routes.filter(isHomeBoundRoute)
  const primarySchoolRoute = schoolRoutes[0] ?? routes[0]
  const primaryHomeRoute = homeRoutes[0] ?? routes[0]

  switch (getHeroPreviewHeroMode(previewMode)) {
    case 'shuttle:imminent': {
      const schedule = primarySchoolRoute.schedules?.[0] ?? null
      return {
        route: primarySchoolRoute,
        nextSchedule: schedule,
        next: schedule ? { minutesLeft: 3, depMin: 508 } : null,
        context: { direction: 'toSchool', reason: 'goSchoolWindow', label: '곧 출발해요' },
      }
    }
    case 'shuttle:missed': {
      const schedule = primarySchoolRoute.schedules?.[1] ?? primarySchoolRoute.schedules?.[0] ?? null
      return {
        route: primarySchoolRoute,
        nextSchedule: schedule,
        next: schedule ? { minutesLeft: 15, depMin: 530 } : null,
        context: { direction: 'toSchool', reason: 'justMissed', label: '방금 출발했어요' },
      }
    }
    case 'shuttle:transit': {
      const schedule = primarySchoolRoute.schedules?.[0] ?? null
      return {
        route: primarySchoolRoute,
        nextSchedule: schedule,
        next: schedule ? { minutesLeft: 20, depMin: 540 } : null,
        context: { direction: 'toSchool', reason: 'inTransit', label: '오늘 학교 가는 셔틀' },
      }
    }
    case 'shuttle:arriving': {
      const schedule = primarySchoolRoute.schedules?.[0] ?? null
      return {
        route: primarySchoolRoute,
        nextSchedule: schedule,
        next: schedule ? { minutesLeft: 35, depMin: 560 } : null,
        context: { direction: 'toSchool', reason: 'arrivingSoon', label: '오늘 학교 가는 셔틀' },
      }
    }
    case 'shuttle:favorite': {
      const schedule = primarySchoolRoute.schedules?.[0] ?? null
      return {
        route: primarySchoolRoute,
        nextSchedule: schedule,
        next: schedule ? { minutesLeft: 10, depMin: 510 } : null,
        context: { direction: 'toSchool', reason: 'favoriteStation', label: '즐겨찾는 노선', isFavoriteHero: true, favoriteStationName: '병점역' },
      }
    }
    case 'shuttle:commute':
    case 'class-next':
    case 'class-ongoing':
    case 'class-next:early-night':
    case 'commute':
    case 'pre-class':
    case 'in-class': {
      const schedule = primarySchoolRoute.schedules?.[0] ?? null
      return {
        route: primarySchoolRoute,
        nextSchedule: schedule,
        next: schedule ? { minutesLeft: 12, depMin: 508 } : null,
        context: { direction: 'toSchool', reason: 'goSchoolWindow', label: '오늘 학교 가는 셔틀' },
      }
    }
    case 'meal': {
      const schedule = primarySchoolRoute.schedules?.[0] ?? null
      return {
        route: primarySchoolRoute,
        nextSchedule: schedule,
        next: schedule ? { minutesLeft: 28, depMin: 720 } : null,
        context: { direction: 'toSchool', reason: 'goSchoolWindow', label: '다음 운행 안내' },
      }
    }
    case 'shuttle:go-home':
    case 'go-home': {
      const schedule = primaryHomeRoute.schedules?.[0] ?? null
      return {
        route: primaryHomeRoute,
        nextSchedule: schedule,
        next: schedule ? { minutesLeft: 8, depMin: 930 } : null,
        context: { direction: 'fromSchool', reason: 'goHomeWindow', label: '오늘 집 가는 셔틀' },
      }
    }
    case 'weekend-empty':
    case 'default:no-service':
    case 'weekend':
    case 'no-service': {
      return {
        route: primarySchoolRoute,
        nextSchedule: null,
        next: null,
        context: { direction: 'toSchool', reason: 'weekend', label: '다음 운행 안내', isWeekend: true },
      }
    }
    case 'default:evening-done':
    case 'night': {
      return {
        route: primarySchoolRoute,
        nextSchedule: null,
        next: null,
        context: { direction: 'toSchool', reason: 'offHours', label: '내일 첫 셔틀' },
      }
    }
    default:
      return null
  }
}

function getHeroSubText(state, minutes) {
  if (state === 'missed') return '이번 차는 놓쳤고 다음 차를 안내할게요'
  if (state === 'ended') return '내일 첫차를 안내할게요'
  if (minutes >= 20) return '지금 나가면 여유 있어요'
  if (minutes >= 10) return '슬슬 이동하면 좋아요'
  if (minutes >= 5) return '지금 출발하면 맞출 수 있어요'
  return '지금 바로 이동하세요'
}

function getHeroStatus(route, nextSchedule, next, context = getShuttleContext(), now = getCurrentMinutes()) {
  const firstTime = route?.schedules?.[0]?.time ?? '--:--'
  const boarding = route?.boardingPoint ?? route?.from ?? ''
  const arrival = route?.to ?? route?.destinationLabel ?? ''

  if (context?.isWeekend) {
    return {
      state: 'ended',
      label: context?.label ?? '다음 운행 안내',
      badge: '주말',
      mainTime: '주말 운행 없음',
      subText: '주말에는 셔틀 운행이 없어요',
      detailText: `${boarding} 출발${arrival ? ` · ${arrival} 도착` : ''}`,
    }
  }

  if (!route?.schedules?.length || !nextSchedule || !next) {
    return {
      state: 'ended',
      label: context?.label ?? '내일 첫 셔틀',
      badge: null,
      mainTime: '오늘 운행 종료',
      subText: getHeroSubText('ended', 0),
      detailText: `${boarding} 출발${arrival ? ` · ${arrival} 도착` : ''}`,
    }
  }

  const previous = [...route.schedules]
    .map(item => ({ item, depMin: timeToMinutes(item.time) }))
    .filter(({ depMin }) => Number.isFinite(depMin) && depMin < now)
    .sort((a, b) => b.depMin - a.depMin)[0]
  const justMissed = previous && now - previous.depMin <= 5

  if (justMissed) {
    return {
      state: 'missed',
      label: context?.isFavoriteHero ? '즐겨찾기 정류장' : '방금 출발했어요',
      badge: '다음 차 안내',
      mainTime: '다음 셔틀 대기 중',
      subText: getHeroSubText('missed', next.minutesLeft ?? 0),
      detailText: `${boarding} 출발${arrival ? ` · ${arrival} 도착` : ''}`,
    }
  }

  const minutes = next.minutesLeft ?? 0
  const state = minutes <= 5 ? 'urgent' : minutes <= 15 ? 'soon' : 'relaxed'
  return {
    state,
    label: context?.isFavoriteHero ? '즐겨찾기 정류장' : minutes <= 5 ? '곧 출발해요' : context?.label ?? '오늘 셔틀',
    badge: null,
    mainTime: minutes === 0 ? '지금 출발' : `${minutes}분 뒤 출발`,
    subText: getHeroSubText(state, minutes),
    detailText: `${nextSchedule.time} · ${boarding} 출발`,
  }
}

function timeToMinutes(time) {
  const [h, m] = String(time ?? '').split(':').map(Number)
  if (!Number.isFinite(h) || !Number.isFinite(m)) return null
  return h * 60 + m
}

function normalizeHeroStopLabel(label, fallback) {
  const text = String(label || fallback || '').trim()
  if (!text) return fallback
  if (/수원대|학교/.test(text)) return '학교'
  return text.replace(/\s*후문$/, '')
}

// 칩 라벨은 더 이상 여기서 자체 추정하지 않고, home 히어로와 같은 단일 소스인
// ShuttleRouteMotion과 같은 presentation state에서 가져온다.
// (과거엔 statusState가 'relaxed'(다음 출발이 몇 시간 남음)일 때도 무조건
// '운행 중'으로 떨어지는 버그가 있었다 — 출발 신뢰 윈도우 게이트가 없었기 때문.)

function getHeroMainStatus(statusState, context) {
  if (context?.isWeekend) return '주말 운행 없음'
  if (statusState === 'urgent' || statusState === 'soon') return '곧 출발해요'
  if (statusState === 'ended') {
    return context?.favoriteStationName
      ? `오늘 ${context.favoriteStationName} 방향 운행 종료`
      : '오늘 운행 종료'
  }
  if (statusState === 'missed') return '다음 셔틀 대기 중'
  return '셔틀 운행 중'
}

function getHeroSupportText({ statusState, context, firstTime, nextSchedule }) {
  if (context?.isWeekend) return `다음 셔틀은 월요일 ${firstTime}`
  if (statusState === 'ended') return `다음 운행일 첫차 ${firstTime}`
  if (nextSchedule?.time) return `다음 셔틀은 ${nextSchedule.time}`
  return `다음 셔틀은 ${firstTime}`
}

function getHeroTimeDisplay(next, nextSchedule, statusState, context) {
  if (context?.isSemesterOver) return '학기 운행 종료'
  if (context?.isWeekend) return '주말 휴무'
  if (statusState === 'ended') return '운행 종료'
  if (statusState === 'missed') return '대기 중'
  return nextSchedule?.time ?? '--:--'
}

function getHeroTimeLabel(statusState, context) {
  if (context?.isSemesterOver) return '운행 안내'
  if (context?.isWeekend || statusState === 'ended') return '오늘 셔틀'
  if (statusState === 'missed') return '다음 차량'
  return '다음 셔틀'
}

function getHeroUntilText(next, statusState, context, firstTime) {
  if (context?.isSemesterOver) return '학기 운행 기간이 종료되었습니다'
  if (context?.isWeekend) return `다음 셔틀은 월요일 ${firstTime}`
  if (statusState === 'ended') return `다음 운행일 첫차 ${firstTime}`
  if (statusState === 'missed') return '다음 차량을 확인해주세요'
  if (!next) return ''
  const mins = next.minutesLeft ?? 0
  if (mins === 0) return '지금 바로 출발이에요'
  if (mins < 60) return `출발까지 약 ${mins}분`
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return m > 0 ? `출발까지 약 ${h}시간 ${m}분` : `출발까지 약 ${h}시간`
}

export function ShuttleHeroCard({ hero, onOpenDetail, isDark, isPreview }) {
  if (!hero?.route) return null

  const { route, nextSchedule, next, context } = hero
  const firstSchedule = route.schedules?.[0]
  const firstTime = firstSchedule?.time ?? '--:--'
  const detailRouteId = nextSchedule?.detailRouteId ?? firstSchedule?.detailRouteId
  const heroStatus = getHeroStatus(route, nextSchedule, next, context)
  const fromLabel = normalizeHeroStopLabel(route.from || route.boardingPoint, '출발지')
  const toLabel   = normalizeHeroStopLabel(route.to || route.destinationLabel, '학교')

  const followingSchedule = nextSchedule?.time
    ? route.schedules?.find(schedule => timeToMinutes(schedule.time) > timeToMinutes(nextSchedule.time))
    : null
  const phase = getShuttleRouteMotionPhase({
    departureTime: nextSchedule?.time ?? null,
    minsUntilDeparture: next?.minutesLeft ?? null,
    isEnded: Boolean(context?.isWeekend || context?.isSemesterOver || !nextSchedule || !next),
  })
  const motionMode = getShuttleRouteMotionMode({
    departureTime: nextSchedule?.time,
    followingDepartureTime: followingSchedule?.time,
  })
  const pillText = getShuttleRouteMotionLabel(phase)
  const timeDisplay = getHeroTimeDisplay(next, nextSchedule, heroStatus.state, context)
  const timeLabel = getHeroTimeLabel(heroStatus.state, context)
  const untilText = getHeroUntilText(next, heroStatus.state, context, firstTime)
  const isSemesterEndedHero = Boolean(context?.isSemesterOver)
  const headerLabel = isSemesterEndedHero
    ? '즐겨찾는 노선'
    : (context?.isFavoriteHero ? '즐겨찾는 노선' : (context?.label ?? '다음 운행 안내'))
  const heroPillText = isSemesterEndedHero ? '운행 종료' : pillText

  function handleOpenDetail() {
    if (detailRouteId) onOpenDetail?.(detailRouteId)
  }

  function handleKeyDown(e) {
    if (e.key !== 'Enter' && e.key !== ' ') return
    e.preventDefault()
    handleOpenDetail()
  }

  return (
    <section
      className={`shuttle-premium-hero ${isDark ? 'is-dark' : ''} ${isSemesterEndedHero ? 'is-semester-ended' : ''}`}
      role="button"
      tabIndex={0}
      aria-label={`${route.name} 경로 상세보기`}
      onClick={handleOpenDetail}
      onKeyDown={handleKeyDown}
    >
      <span className="shuttle-premium-hero__aurora aurora-a" aria-hidden="true" />
      <span className="shuttle-premium-hero__aurora aurora-b" aria-hidden="true" />
      <span className="shuttle-premium-hero__aurora aurora-c" aria-hidden="true" />
      <span className="shuttle-premium-hero__diagonal-shine" aria-hidden="true" />
      <span className="shuttle-premium-hero__shine" aria-hidden="true" />

      <div className="shuttle-premium-hero__header">
        <span className="shuttle-premium-hero__label">
          {headerLabel}
          {isPreview && <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 800, color: '#f59e0b', background: 'rgba(245,158,11,0.12)', borderRadius: 999, padding: '1px 6px' }}>Preview</span>}
        </span>
        <span className="shuttle-premium-hero__pill">
          {!isSemesterEndedHero && <span className="shuttle-premium-hero__status-dot" aria-hidden="true" />}
          {heroPillText}
        </span>
      </div>

      <div className="shuttle-premium-hero__body">
        <p className="shuttle-premium-hero__route">{route.name}</p>
        {isSemesterEndedHero ? (
          <>
            <p className="shuttle-premium-hero__ended-main">2학기 운행을 준비하고 있어요</p>
            <p className="shuttle-premium-hero__ended-sub">9월 1일부터 다시 운행해요</p>
          </>
        ) : (
          <>
            <div className="shuttle-premium-hero__time-block">
              <span className="shuttle-premium-hero__time-label">{timeLabel}</span>
              <span className="shuttle-premium-hero__status">{timeDisplay}</span>
            </div>
            <p className="shuttle-premium-hero__support">{untilText}</p>
          </>
        )}
      </div>

      <div className="shuttle-premium-hero__route-map">
        <ShuttleRouteMotion
          from={fromLabel}
          to={toLabel}
          departureTime={nextSchedule?.time ?? null}
          minsUntilDeparture={next?.minutesLeft ?? null}
          busCount={nextSchedule?.buses ?? 1}
          followingDepartureTime={followingSchedule?.time ?? null}
          visualPhase={phase}
          mode={motionMode}
          isEnded={Boolean(context?.isWeekend || context?.isSemesterOver || !nextSchedule || !next)}
          isDark={isDark}
        />
      </div>
    </section>
  )
}

// ── 서브 컴포넌트 ─────────────────────────────────────────────────────────────

function InfoBanner({ children, isDark }) {
  return (
    <div className={`flex gap-1.5 rounded-[12px] px-3 py-2 text-[10px] leading-snug
                     ${isDark ? 'bg-[#1c2a3a] text-[#7ab5e0]' : 'bg-[#f0f6ff] text-[#4a7fa8]'}`}>
      {children}
    </div>
  )
}

function ShuttleSearchBar({ query, onQueryChange, onClear, isDark }) {
  const [focused, setFocused] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const inputRef = useRef(null)
  const mutedText = isDark ? 'text-[#8e8e93]' : 'text-[#64748b]'

  function openSearch() {
    setExpanded(true)
    window.setTimeout(() => inputRef.current?.focus(), 80)
  }

  function closeSearch() {
    onClear()
    setExpanded(false)
    setFocused(false)
  }

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex items-center justify-end">
        <button
          type="button"
          onClick={openSearch}
          className={`h-8 w-8 shrink-0 rounded-full border flex items-center justify-center active:scale-95 transition
                      ${query
                        ? isDark ? 'border-[#1f4f7a] bg-[#0d1f33] text-[#2997ff]' : 'border-[#d8e9ff] bg-[#e8f2ff] text-[#0066cc]'
                        : isDark ? 'border-[#2c2c2e] bg-[#1c1c1e] text-[#aeaeb2]' : 'border-[#edf1f5] bg-white text-[#64748b]'}`}
          aria-label="셔틀 검색 열기"
          aria-expanded={expanded}
        >
          <Search size={16} strokeWidth={1.85} />
        </button>
      </div>

      {query && !expanded && (
        <p className={`px-1 text-[10px] font-semibold ${mutedText}`}>
          검색 중: {query}
        </p>
      )}

      <div
        className={`grid transition-all duration-200 ease-out ${expanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
        aria-hidden={!expanded}
      >
        <div className="overflow-hidden">
          <div
            className={`h-10 rounded-full px-3 flex items-center gap-2 border transition-all
                        ${focused
                          ? isDark ? 'shadow-[0_0_0_3px_rgba(10,132,255,0.18)] border-[#1f4f7a]' : 'shadow-[0_4px_18px_rgba(0,102,204,0.12)] border-[#d8e9ff]'
                          : isDark ? 'bg-[#1c1c1e] border-[#2c2c2e] shadow-none' : 'bg-white border-[#f1f5f9] shadow-sm'}`}
          >
            <Search size={16} strokeWidth={1.85} className={isDark ? 'text-[#8e8e93]' : 'text-[#9ca3af]'} />
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder="출발지·도착지 검색"
              className={`flex-1 min-w-0 bg-transparent border-0 outline-none text-[14px] font-semibold
                          ${isDark ? 'text-[#f5f5f7] placeholder:text-[#636366]' : 'text-[#1d1d1f] placeholder:text-[#9ca3af]'}`}
            />
            <button
              type="button"
              onClick={closeSearch}
              className={`w-7 h-7 rounded-full text-xs font-bold active:opacity-70 transition-colors
                          ${isDark ? 'bg-[#2c2c2e] text-[#aeaeb2]' : 'bg-[#f5f5f7] text-[#6b7280] hover:bg-[#eef2f7]'}`}
              aria-label="검색어 지우고 닫기"
            >
              <X size={14} strokeWidth={1.9} className="mx-auto" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── 메인 컴포넌트 ─────────────────────────────────────────────────────────────

export default function ShuttleTab({ t, isDark, onOpenDetail, stationFocus, onStationFocusConsumed }) {
  const { user } = useAuth()
  const userId = user?.id ?? null
  const { heroPreviewMode, isHeroPreviewActive } = useHeroPreview()
  const [favoriteIds, setFavoriteIds] = useState(() => getFavoriteShuttleRoutes())
  const [isEditingFrequent, setIsEditingFrequent] = useState(false)
  const [favoriteNotice, setFavoriteNotice] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')
  // undefined means the initial state has not been overridden by the user;
  // an array (including empty []) is an explicit multi-expand accordion state.
  const [expandedStationKeys, setExpandedStationKeys] = useState(undefined)
  const initialExpandedStationKeysRef = useRef([])
  const [highlightedStationKey, setHighlightedStationKey] = useState(null)
  const [, forceUpdate] = useState(0)
  const highlightTimerRef = useRef(null)

  useEffect(() => {
    const id = setInterval(() => forceUpdate(n => n + 1), 60000)
    return () => clearInterval(id)
  }, [])

  // 셔틀 화면 상단에서 시작 (stationFocus 진입 시에는 대상 스크롤 우선)
  useEffect(() => {
    if (stationFocus?.stationId) return
    const el = document.querySelector('.app-main')
    if (el && el.scrollTop !== 0) el.scrollTop = 0
    if (document.documentElement.scrollTop !== 0) document.documentElement.scrollTop = 0
    window.scrollTo(0, 0)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 캠퍼스맵 "셔틀 시간표 보기" → 해당 정류장 카드 자동 펼침 + 스크롤 + 1초 하이라이트
  useEffect(() => {
    if (!stationFocus?.stationId) return
    const stationKey = STATION_ID_TO_KOREAN[stationFocus.stationId]
    if (!stationKey) { onStationFocusConsumed?.(); return }

    setExpandedStationKeys(current => {
      const base = getVisibleExpandedStationKeys(current, initialExpandedStationKeysRef.current)
      return base.includes(stationKey) ? base : [...base, stationKey]
    })
    setHighlightedStationKey(stationKey)

    const scrollTimer = window.setTimeout(() => {
      document.getElementById(`station-${stationKey}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }, 80)

    if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current)
    highlightTimerRef.current = window.setTimeout(() => setHighlightedStationKey(null), 1000)

    onStationFocusConsumed?.()
    return () => window.clearTimeout(scrollTimer)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stationFocus])

  useEffect(() => () => { if (highlightTimerRef.current) window.clearTimeout(highlightTimerRef.current) }, [])

  /**
   * routeId + 선택적 meta(label) 를 받아 즐겨찾기를 토글합니다.
   * meta 를 전달하면 suguide_favorite_shuttles 에 { routeId, stopId, label } 형태로 저장됩니다.
   */
  const handleToggleFavorite = useCallback((routeId, meta) => {
    const wasFavorite = favoriteIds.includes(routeId)
    const updated = toggleFavoriteRoute(routeId, meta)
    setFavoriteIds(updated)
    setFavoriteNotice(wasFavorite
      ? '즐겨찾는 노선에서 삭제했어요'
      : '즐겨찾는 노선에 추가했어요 · 홈에서도 확인할 수 있어요')
  }, [favoriteIds])

  useEffect(() => {
    if (!favoriteNotice) return undefined
    const timeoutId = window.setTimeout(() => setFavoriteNotice(null), 2600)
    return () => window.clearTimeout(timeoutId)
  }, [favoriteNotice])

  const handleToggleStation = useCallback((stationKey) => {
    setExpandedStationKeys(current =>
      toggleExpandedStationKeys(current, stationKey, initialExpandedStationKeysRef.current)
    )
  }, [])

  const handleToggleEditFrequent = useCallback(() => {
    setIsEditingFrequent(current => !current)
  }, [])

  const { routes = [], notices, source, lastUpdated, confidence } = shuttleData

  const sortedRoutes = sortShuttleRoutes(routes)
  const unifiedRoutes = getUnifiedShuttleRoutes(sortedRoutes)
  const heroRoute = isHeroPreviewActive
    ? (getPreviewShuttleHero(unifiedRoutes, heroPreviewMode) ?? getSmartShuttleHeroRoute(unifiedRoutes, favoriteIds, new Date(), userId))
    : getSmartShuttleHeroRoute(unifiedRoutes, favoriteIds, new Date(), userId)
  const activeHeroRouteId = heroRoute?.route?.id ?? null
  const stationGroups = buildStationGroups(unifiedRoutes)
  const activeHeroStationKey = stationGroups.find(group =>
    Object.values(group.directions).some(direction => direction.routeId === activeHeroRouteId)
  )?.stationKey ?? null
  const filteredGroups = filterStationGroups(stationGroups, searchQuery, '전체')
  const orderedFilteredGroups = activeHeroStationKey
    ? [
        ...filteredGroups.filter(group => group.stationKey === activeHeroStationKey),
        ...filteredGroups.filter(group => group.stationKey !== activeHeroStationKey),
      ]
    : filteredGroups

  const favoriteStationKeys = stationGroups
    .filter(group => Object.values(group.directions).some(direction => favoriteIds.includes(direction.routeId)))
    .sort((a, b) => {
      const aIndex = favoriteIds.findIndex(id => Object.values(a.directions).some(direction => direction.routeId === id))
      const bIndex = favoriteIds.findIndex(id => Object.values(b.directions).some(direction => direction.routeId === id))
      return aIndex - bIndex
    })
    .map(group => group.stationKey)
  const favoriteGroups = favoriteStationKeys
    .map(key => stationGroups.find(group => group.stationKey === key))
    .filter(Boolean)
  const remainingGroups = orderedFilteredGroups.filter(group => !favoriteStationKeys.includes(group.stationKey))
  const initialExpandedStationKeys = getInitialExpandedStationKeys(favoriteGroups, remainingGroups)
  initialExpandedStationKeysRef.current = initialExpandedStationKeys
  const visibleExpandedStationKeys = getVisibleExpandedStationKeys(expandedStationKeys, initialExpandedStationKeys)

  if (routes.length === 0) {
    return (
      <div className={`${t.card} border rounded-[16px] p-6 text-center`}>
        <BusFront size={24} strokeWidth={1.9} className={`mx-auto mb-2 ${t.muted}`} />
        <p className={`text-sm font-semibold mb-1`}>현재 등록된 셔틀 시간표가 없습니다</p>
        <p className={`text-xs ${t.muted}`}>데이터를 불러오지 못했습니다. 공식 홈페이지를 확인하세요.</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-2.5">
      {confidence === 'manual_from_image' && (
        <div className={`flex gap-1.5 rounded-[12px] px-3 py-2 text-[10px] leading-snug
                         ${isDark ? 'bg-[#2a2518] text-[#c8aa60]' : 'bg-[#fffbea] text-[#8a6d00]'}`}>
          <AlertTriangle size={15} strokeWidth={1.85} className="mt-0.5 shrink-0" />
          <span>공식 이미지 시간표 기반 수동 입력 데이터입니다. 반드시 공식 홈페이지에서 확인하세요.</span>
        </div>
      )}

      <ShuttleHeroCard
        hero={heroRoute}
        onOpenDetail={onOpenDetail}
        isDark={isDark}
        isPreview={isHeroPreviewActive}
      />

      {favoriteGroups.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2 mt-[18px]">
            <div>
              <p className={`text-[11px] font-semibold ${t.muted}`}>즐겨찾는 노선</p>
              <p className={`mt-0.5 text-[10px] ${t.muted}`}>홈에서 빠르게 확인할 노선</p>
            </div>
            <button
              type="button"
              onClick={handleToggleEditFrequent}
              aria-label={isEditingFrequent ? '즐겨찾는 노선 관리 완료' : '즐겨찾는 노선 관리'}
              className="flex min-h-[44px] min-w-[44px] items-center justify-center p-0"
            >
              {isEditingFrequent ? (
                <span className="inline-flex h-8 items-center rounded-2xl bg-[#0a7cff] px-3.5 text-[12px] font-bold text-white shadow-sm active:bg-[#0066cc]">
                  완료
                </span>
              ) : (
                <span className={`text-[12px] font-semibold ${isDark ? 'text-[#b6bcc7]' : 'text-[#475569]'}`}>
                  관리
                </span>
              )}
            </button>
          </div>

          <div className="flex flex-col gap-2.5">
            {favoriteGroups.map(group => (
                <ShuttleStationCard
                  key={group.stationKey}
                  group={group}
                  t={t}
                  isDark={isDark}
                  favoriteIds={favoriteIds}
                  showFavorite={isEditingFrequent}
                  isHeroStation={group.stationKey === activeHeroStationKey}
                  isExpanded={visibleExpandedStationKeys.includes(group.stationKey)}
                  isHighlighted={group.stationKey === highlightedStationKey}
                  onToggleExpanded={handleToggleStation}
                  onToggleFavorite={handleToggleFavorite}
                  onOpenDetail={onOpenDetail}
                />
            ))}
          </div>
        </div>
      )}

      <div>
        <p className={`text-[11px] font-semibold mb-2 mt-[18px] ${t.muted}`}>전체 노선</p>

        {remainingGroups.length === 0 ? (
          <div className={`${t.card} border rounded-[16px] px-4 py-6 text-center`}>
            <p className="text-sm font-bold">검색 결과가 없어요</p>
            <p className={`text-[11px] mt-1 ${t.muted}`}>다른 출발지나 도착지를 입력해보세요</p>
          </div>
        ) : (
          <div className="flex flex-col gap-2.5">
            {remainingGroups.map(group => (
              <ShuttleStationCard
                key={group.stationKey}
                group={group}
                t={t}
                isDark={isDark}
                favoriteIds={favoriteIds}
                isHeroStation={group.stationKey === activeHeroStationKey}
                isExpanded={visibleExpandedStationKeys.includes(group.stationKey)}
                isHighlighted={group.stationKey === highlightedStationKey}
                onToggleExpanded={handleToggleStation}
                onToggleFavorite={handleToggleFavorite}
                onOpenDetail={onOpenDetail}
              />
            ))}
          </div>
        )}
      </div>

      {favoriteNotice && (
        <div role="status" className="fixed bottom-24 left-1/2 z-50 -translate-x-1/2 rounded-full bg-[#1d1d1f] px-4 py-2 text-[11px] font-semibold text-white shadow-lg">
          {favoriteNotice}
        </div>
      )}

      {notices && notices.length > 0 && (
        <div className={`${t.card} border rounded-[16px] p-4`}>
          <h3 className={`flex items-center gap-1.5 text-xs font-semibold mb-2 ${t.sub}`}>
            <AlertTriangle size={15} strokeWidth={1.85} />
            운행 유의사항
          </h3>
          <div className="flex flex-col gap-1.5">
            {notices.map((n, i) => (
              <p key={i} className={`text-[10px] leading-relaxed ${t.muted}`}>• {n}</p>
            ))}
          </div>
        </div>
      )}

      <div className={`flex items-center justify-center gap-1 text-center text-[10px] pb-1 ${t.muted}`}>
        <Info size={12} strokeWidth={1.85} />
        <span>{source} 기준 · {lastUpdated} 업데이트 · 2026학년도 2학기 중 평일 운행</span>
      </div>

      <div className="flex items-center justify-center pb-1">
        <a
          href="https://www.suwon.ac.kr/index.html?act=view&bbsno=88352&boardno=667&menuno=674&siteno=37"
          target="_blank"
          rel="noopener noreferrer"
          className={`rounded-full px-3 py-1 text-[10px] font-semibold border transition-colors
                      ${isDark ? 'border-[#1f4f7a] bg-[#0d1f33] text-[#2997ff]' : 'border-[#d8e9ff] bg-[#e8f2ff] text-[#0066cc]'}`}
        >
          공식 원문 보러가기
        </a>
      </div>
    </div>
  )
}
